import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { FixedContainer } from '@calendly/ui/components/fixed-container';
import classNames from 'classnames';
import { forwardRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useIntercomPosition } from 'components/external/intercom_chat_bot/useIntercomPosition';
import { useTrack, TRACKING_EVENT_NAMES } from 'hooks/analytics_events';
import { useAiChatDiscoveryFabVisibility } from './ai_chat_discovery_fab_visibility';
import { AiChatDiscoveryEntrySpotlight } from './AiChatDiscoveryEntrySpotlight';
import { AI_CHAT_DISCOVERY_FAB_APPEARANCE, AI_CHAT_DISCOVERY_NAVIGATION_LOCATION, AI_CHAT_DISCOVERY_SPOTLIGHT_FLOW_ID, AI_CHAT_DISCOVERY_SPOTLIGHT_KEY } from './constants';
import { clearMeetingsSelectionFromUrlIfPresent, closeAiChatRailPane, openAiChatRailPane } from './utils';
const fabBaseCss = "f16fo6nl";
const ButtonFixedContainer = forwardRef(({
  className,
  rightOffsetPx,
  bottomOffsetPx,
  ...rest
}, ref) => <FixedContainer ref={ref} {...rest} style={{
  right: `${rightOffsetPx}px`,
  bottom: `${bottomOffsetPx}px`
}} className={classNames(fabBaseCss, className)} data-ai-discovery-fab-right-offset={rightOffsetPx} data-ai-discovery-fab-bottom-offset={bottomOffsetPx} />);
const INTERCOM_BUTTON_WIDTH = 48;
const FLOATING_FAB_HORIZONTAL_GAP_PX = 8;
/** Phone corner inset; desktop bottom uses `verticalPadding` from Intercom + `FAB_BOTTOM_ABOVE_INTERCOM_PX`. */
const DEFAULT_OFFSET_PX = 24;
const FAB_BOTTOM_ABOVE_INTERCOM_PX = 4;
export const AiChatDiscoveryFloatingButton = () => {
  const track = useTrack();
  const dispatch = useDispatch();
  const {
    search
  } = useLocation();
  // TODO: in SA-3840 try to merge these two hooks
  const {
    showFab,
    fabAppearance,
    replaceAndClearStack,
    close,
    paneName,
    current
  } = useAiChatDiscoveryFabVisibility();
  const {
    horizontalPadding,
    verticalPadding,
    isPhone
  } = useIntercomPosition();
  const rightOffsetPx = useMemo(() => isPhone ? DEFAULT_OFFSET_PX : horizontalPadding + INTERCOM_BUTTON_WIDTH + FLOATING_FAB_HORIZONTAL_GAP_PX, [horizontalPadding, isPhone]);
  const bottomOffsetPx = useMemo(() => isPhone ? DEFAULT_OFFSET_PX : verticalPadding + FAB_BOTTOM_ABOVE_INTERCOM_PX, [isPhone, verticalPadding]);
  if (!showFab) {
    return null;
  }
  const toggleAiChatRail = () => {
    if (paneName === 'ai_chat') {
      // clear selection so `meeting_details` does not re-open from URL
      clearMeetingsSelectionFromUrlIfPresent(dispatch, search);
      closeAiChatRailPane(close, current);
    } else {
      track({
        name: TRACKING_EVENT_NAMES.NAVIGATION_LINK_CLICKED,
        properties: {
          navigation_link: 'ai_assistant',
          navigation_location: AI_CHAT_DISCOVERY_NAVIGATION_LOCATION.BOTTOM_RIGHT_FAB,
          source_url: window.location.href
        }
      });
      openAiChatRailPane(replaceAndClearStack);
    }
  };
  const stringLabel = I18n.t('publisher.agent_hub.discovery_fab.string_label');
  const iconAriaLabel = I18n.t('publisher.agent_hub.discovery_fab.icon_aria_temp');
  if (fabAppearance === AI_CHAT_DISCOVERY_FAB_APPEARANCE.ICON) {
    return <ButtonFixedContainer rightOffsetPx={rightOffsetPx} bottomOffsetPx={bottomOffsetPx}>
        <AiChatDiscoveryEntrySpotlight spotlightKey={AI_CHAT_DISCOVERY_SPOTLIGHT_KEY.BOTTOM_RIGHT_FAB} flowId={AI_CHAT_DISCOVERY_SPOTLIGHT_FLOW_ID.BOTTOM_RIGHT_FAB} isEntryVisible={showFab}>
          <Button decoration="ai" onClick={toggleAiChatRail} aria-label={iconAriaLabel} data-calendly-label="ai-chat-discovery-fab-icon" data-testid="ai-chat-discovery-fab-icon" />
        </AiChatDiscoveryEntrySpotlight>
      </ButtonFixedContainer>;
  }
  if (fabAppearance === AI_CHAT_DISCOVERY_FAB_APPEARANCE.STRING) {
    return <ButtonFixedContainer rightOffsetPx={rightOffsetPx} bottomOffsetPx={bottomOffsetPx}>
        <AiChatDiscoveryEntrySpotlight spotlightKey={AI_CHAT_DISCOVERY_SPOTLIGHT_KEY.BOTTOM_RIGHT_FAB} flowId={AI_CHAT_DISCOVERY_SPOTLIGHT_FLOW_ID.BOTTOM_RIGHT_FAB} isEntryVisible={showFab}>
          <Button decoration="ai" onClick={toggleAiChatRail} data-calendly-label="ai-chat-discovery-fab-string" data-testid="ai-chat-discovery-fab-string">
            {stringLabel}
          </Button>
        </AiChatDiscoveryEntrySpotlight>
      </ButtonFixedContainer>;
  }
  return null;
};

require("./AiChatDiscoveryFloatingButton.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./AiChatDiscoveryFloatingButton.js");